import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import { ToastProvider } from './components/Toast'
import Navbar from './components/Navbar'
import HomePage from './pages/HomePage'
import CulturePage from './pages/CulturePage'
import WriterProfilePage from './pages/WriterProfilePage'
import PreviewPage from './pages/PreviewPage'
import { Navigate } from 'react-router-dom'
import AuthPage from './pages/AuthPage'
import PostPage from './pages/PostPage'
import WritePage from './pages/WritePage'
import DashboardPage from './pages/DashboardPage'
import AdminPage from './pages/AdminPage'
import ModerationPage from './pages/ModerationPage'
import { AboutPage } from './pages/StaticPages'
import CommunityPage from './pages/CommunityPage'
import SubmissionsPage from './pages/SubmissionsPage'
import MastheadPage from './pages/MastheadPage'
import PromptsPage from './pages/PromptsPage'
import TheRecordPage from './pages/TheRecordPage'
import NotFoundPage from './pages/NotFoundPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import SupportPage from './pages/SupportPage'
import CookieBanner from './components/CookieBanner'
import { useAuth } from './context/AuthContext'
import { useState, useEffect } from 'react'
import './index.css'

export const FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSei7zcQ7kRy7yK-FrBC5EqgaFD1zuYiQJWLsxdwIJFNTnki7w/viewform'

function ScrollProgress() {
  const [progress, setProgress] = useState(0)
  useEffect(() => {
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement
      const total = scrollHeight - clientHeight
      setProgress(total > 0 ? (scrollTop / total) * 100 : 0)
    }
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [])
  return <div className="scroll-progress" style={{ width: `${progress}%` }} />
}

import usePageTracking from './lib/usePageTracking'

function AppContent() {
  const { loading } = useAuth()
  const location = useLocation()
  usePageTracking()

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  // Don't block the whole app — public pages render immediately,
  // auth-required pages handle their own loading state
  return (
    <>
      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @keyframes cursorBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .article-card-hover:hover { background: var(--cream) !important; }
        a:hover { opacity: 0.85; }
        details summary::-webkit-details-marker { color: var(--accent); }
        * { -webkit-font-smoothing: antialiased; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: var(--cream); }
        ::-webkit-scrollbar-thumb { background: var(--rule); }
        ::-webkit-scrollbar-thumb:hover { background: var(--muted); }
        .nav-dropdown-item:hover { background: var(--cream) !important; }
        @media (max-width: 640px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .section-padding { padding: 3rem 1.25rem !important; }
          .article-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <ScrollProgress />
      <Navbar />
      <CookieBanner />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/feed" element={<CulturePage />} />
        <Route path="/culture" element={<Navigate to="/feed" replace />} />
        <Route path="/journal" element={<Navigate to="/feed" replace />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/post/:id" element={<PostPage />} />
        <Route path="/write" element={<WritePage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/moderation" element={<ModerationPage />} />
        <Route path="/writer/:id" element={<WriterProfilePage />} />
        <Route path="/submit" element={<Navigate to="/feed" replace />} />
        <Route path="/join" element={<Navigate to="/about" replace />} />
        <Route path="/social" element={<Navigate to="/about" replace />} />
        <Route path="/submissions" element={<SubmissionsPage />} />
        <Route path="/preview/:token" element={<PreviewPage />} />
        <Route path="/masthead" element={<MastheadPage />} />
        <Route path="/prompts" element={<PromptsPage />} />
        <Route path="/record/:id" element={<TheRecordPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <footer className="footer-inner">
        <div>
          <div style={footerStyles.logo}>
            <svg viewBox="0 0 198 52" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', height: 28, width: 'auto', overflow: 'visible' }}>
              <text x="0" y="22" fontFamily="'Playfair Display', Georgia, 'Times New Roman', serif" fontWeight="700" fontSize="22" fill="var(--ink)" letterSpacing="-0.3">The</text>
              <text x="0" y="48" fontFamily="'Playfair Display', Georgia, 'Times New Roman', serif" fontWeight="700" fontSize="22" fill="var(--ink)" letterSpacing="-0.3">Margins<tspan fill="#C8401A" letterSpacing="0">.</tspan></text>
            </svg>
          </div>
          <p style={footerStyles.tagline}>the place for everything you never said.</p>
        </div>
        <div className="footer-links-group">
          <div style={footerStyles.linkCol}>
            <div style={footerStyles.linkColLabel}>Sections</div>
            <a href="/feed" style={footerStyles.link}>the feed</a>
            <a href="/prompts" style={footerStyles.link}>prompts</a>
          </div>
          <div style={footerStyles.linkCol}>
            <div style={footerStyles.linkColLabel}>About</div>
            <a href="/about" style={footerStyles.link}>about</a>
            <a href="/masthead" style={footerStyles.link}>masthead</a>
            <a href="/community" style={footerStyles.link}>community</a>
            <a href="/dashboard" style={footerStyles.link}>dashboard</a>
          </div>
          <div style={footerStyles.linkCol}>
            <div style={footerStyles.linkColLabel}>Legal</div>
            <a href="/privacy" style={footerStyles.link}>privacy policy</a>
            <a href="/terms" style={footerStyles.link}>terms of service</a>
            <a href="/support" style={footerStyles.link}>support</a>
          </div>
          <div style={footerStyles.linkCol}>
            <div style={footerStyles.linkColLabel}>Social</div>
            <a href="https://www.linkedin.com/company/the-margins-journal/" target="_blank" rel="noreferrer" style={footerStyles.link}>LinkedIn</a>
            <a href="https://www.instagram.com/themargins_journal/" target="_blank" rel="noreferrer" style={footerStyles.link}>Instagram</a>
            <a href="https://discord.gg/MRbakEXjPM" target="_blank" rel="noreferrer" style={footerStyles.link}>Discord</a>
          </div>
        </div>
      </footer>
      <div className="footer-bottom">
        <p style={footerStyles.copy}>© 2026 The Margins. say something real.</p>
      </div>
    </>
  )
}

const footerStyles = {
  footer: {
    padding: '3rem 3rem 2rem',
    borderTop: '1px solid var(--rule)',
    background: 'var(--cream)',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '2rem',
    flexWrap: 'wrap',
  },
  logo: {
    marginBottom: '0.4rem',
  },
  tagline: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.62rem', letterSpacing: '0.1em',
    textTransform: 'uppercase', color: 'var(--muted)',
  },
  linksGroup: {
    display: 'flex', gap: '3rem',
  },
  linkCol: {
    display: 'flex', flexDirection: 'column', gap: '0.5rem',
  },
  linkColLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.58rem', letterSpacing: '0.14em',
    textTransform: 'uppercase', color: 'var(--accent)',
    marginBottom: '0.25rem',
  },
  link: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.65rem', letterSpacing: '0.08em',
    textTransform: 'uppercase', color: 'var(--muted)',
    transition: 'color 0.2s',
  },
  bottom: {
    gridColumn: '1 / -1',
    borderTop: '1px solid var(--rule)',
    paddingTop: '1.5rem',
    marginTop: '0.5rem',
  },
  copy: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.6rem', color: 'var(--muted)',
  },
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
